import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Loader from '../../components/common/Loader/Loader';
import UserProfileMainPage from '../../components/user-profile-main/UserProfileMainPage';
import { getShortlist, setBucket } from '../../api/curation';
import styles from './styles';

// Same modal geometry as Bot Coverage / Messaging Activity so the profile
// popup reads as the one admin-wide component it is.
const PROFILE_MODAL_SX = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 1300,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: '0 30px 30px 30px',
  maxHeight: '80vh',
  overflow: 'auto',
};

/**
 * AI Shortlist — the profile-curation review board.
 *
 * Three buckets resolved SERVER-side (an admin correction outranks the AI):
 *   Approved  = elite shortlist: premium photo + an observable affluence signal
 *   Maybe     = photo approved (composed, groomed) but no elite signal
 *   Rejected  = photo reject: selfie / dark / AI-generated / low-effort
 *
 * Moving a card calls POST /admin/curation/bucket/:reviewId and re-renders
 * optimistically; the correction is stored on the review doc (adminBucket) and
 * NEVER touches the member's user doc — this board records decisions, the
 * hide/apply step is a separate, deliberate action.
 *
 * Verdicts come from Fable 5 via the acadeserver gateway; the rubric was
 * calibrated against the admin team's own hide/keep history (2026-08).
 */
const ORDER = ['approved', 'maybe', 'rejected'];
const LABEL = { approved: 'Approve', maybe: 'Maybe', rejected: 'Reject' };
// Same filter vocabulary as the other profile pages (Bot Coverage et al.).
const LOCATIONS = [['all', 'All'], ['us', 'United States'], ['ncr', 'Delhi NCR']];
const JOINED = [['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days'], ['all', 'All time'], ['custom', 'Custom']];
const TODAY = new Date().toISOString().slice(0, 10);
// Sort vocabulary. Default is newest-judged: a profile that just completed and
// got judged belongs at the top of its column, which is what the board is for.
// Score order is a poor default here because scores tie enormously (619 cards
// share score 25), so score-desc leaves the top of a column essentially
// arbitrary and stale.
const DEFAULT_SORT = 'judged-desc';
const SORTS = [
  ['judged-desc', 'Newest judged'],
  ['joined-desc', 'Newest members'],
  ['score-desc', 'Score: high to low'],
  ['score-asc', 'Score: low to high'],
  ['name-asc', 'Name: A to Z'],
];
// "Newest members" needs memberCreatedAt, which older servers do not send. It is
// feature-detected off the payload rather than assumed, so an admin build
// running ahead of the server hides the option instead of offering a sort that
// silently does nothing.
const SORT_NEEDS = { 'joined-desc': 'memberCreatedAt' };
const at = (v) => (v ? new Date(v).getTime() : 0);
// Scoreless cards sink in BOTH directions -- "lowest score first" must not be
// answered with cards that have no score at all.
const byScore = (dir) => (a, b) => {
  const av = typeof a.score === 'number' ? a.score : null;
  const bv = typeof b.score === 'number' ? b.score : null;
  if (av === null || bv === null) {
    if (av === bv) return at(b.reviewedAt) - at(a.reviewedAt);
    return av === null ? 1 : -1;
  }
  return (dir === 'asc' ? av - bv : bv - av) || at(b.reviewedAt) - at(a.reviewedAt);
};
const COMPARE = {
  'judged-desc': (a, b) => at(b.reviewedAt) - at(a.reviewedAt) || byScore('desc')(a, b),
  'joined-desc': (a, b) => at(b.memberCreatedAt) - at(a.memberCreatedAt) || at(b.reviewedAt) - at(a.reviewedAt),
  'score-desc': byScore('desc'),
  'score-asc': byScore('asc'),
  'name-asc': (a, b) => {
    const an = String(a.name || '').trim();
    const bn = String(b.name || '').trim();
    if (!an || !bn) return an === bn ? 0 : (an ? -1 : 1);
    return an.localeCompare(bn, undefined, { sensitivity: 'base' }) || at(b.reviewedAt) - at(a.reviewedAt);
  },
};
const TITLE = { approved: 'Approved', maybe: 'Maybe', rejected: 'Rejected' };
// Each column paints PAGE_SIZE cards and grows as its sentinel scrolls into view.
const PAGE_SIZE = 60;
// Free-text search is a server param now, like Location and Joined. It used to
// run in the browser over the one fetched payload, which was honest only while
// every bucket fitted under the per-bucket cap; past that a search silently
// stopped at the loaded slice. Searching the AI's own evidence matters as much
// as name/email: the signal vocabulary is free text with thousands of distinct
// values, so it can only ever be reached by typing, never by a dropdown.
//
// This haystack stays as the FALLBACK for a server that predates `q` and
// returns everything regardless. It must keep matching the field list the
// server joins, or the two paths would answer the same query differently.
const haystackOf = (it) => [it.name, it.email, it.city, it.reason, ...(it.signals || [])]
  .filter(Boolean).join(' ').toLowerCase();
const NOTE = {
  approved: 'Elite shortlist — class/affluence signals in the photo',
  maybe: 'Clean composed photo, no elite signal',
  rejected: 'Selfie, dark, AI-generated, or low-effort photo',
};

// Infinite-scroll sentinel: sits under a column's last card and reveals the
// next slice whenever it scrolls into view.
const Sentinel = ({ onVisible }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onVisible();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [onVisible]);
  return <div ref={ref} className="ash-sentinel">loading more…</div>;
};

const initials = (name = '') => {
  const p = String(name).trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
};

const Card = ({ item, bucket, onMove, onOpen, busy }) => {
  // Compact by default: avatar, name, city, score, moves. The ⋯ toggle expands
  // the AI's evidence (signal tags + reason + correction note) in place.
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="ash-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item.userId)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(item.userId); }}
    >
      <div className="ash-row1">
        <div className="ash-who">
          <div className="ash-avatar">
            {item.profilePicture ? (
              <img
                src={item.profilePicture}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              initials(item.name)
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="ash-name">
              <span className="ash-name-text">{item.name || '—'}{item.age ? `, ${item.age}` : ''}</span>
              {item.elite ? <span className="ash-flag elite">ELITE</span> : null}
              {item.pendingAttempt ? <span className="ash-flag pay" title="Attempted checkout — tried to pay once already">TRIED TO PAY</span> : null}
              {item.manualHidden ? <span className="ash-flag manual" title="Hidden from User Management — unhide there, not here">HIDDEN BY ADMIN</span> : null}
              {item.previous ? (
                <span
                  className="ash-flag reeval"
                  title={`Photo changed ${item.previous.replacedAt ? new Date(item.previous.replacedAt).toLocaleDateString() : ''} — previous photo was ${item.previous.verdict === 'approved' ? (item.previous.elite ? 'Approved (elite)' : 'Approved') : 'Rejected'}${item.previous.reviewedAt ? ` on ${new Date(item.previous.reviewedAt).toLocaleDateString()}` : ''}. This verdict is for the new photo.`}
                >
                  ✱ RE-EVALUATED
                </span>
              ) : null}
              <button
                className="ash-dots"
                title={expanded ? 'Hide details' : 'Show AI reasoning'}
                aria-expanded={expanded}
                onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              >
                …
              </button>
            </div>
            <div className="ash-meta">{item.city || ''}</div>
          </div>
        </div>
        <div className="ash-score">{item.score ?? '–'}<small> /100</small></div>
      </div>
      {expanded ? (
        <>
          {item.signals?.length ? (
            <div className="ash-tags">
              {item.signals.map((s, i) => <span key={i} className="ash-tag">{s}</span>)}
            </div>
          ) : null}
          {item.reason ? <div className="ash-reason">{item.reason}</div> : null}
          {item.previous ? (
            <div className="ash-corrected">
              ✱ photo changed — previous photo was {item.previous.verdict === 'approved' ? (item.previous.elite ? 'approved (elite)' : 'approved') : 'rejected'}
              {item.previous.reviewedAt ? ` on ${new Date(item.previous.reviewedAt).toLocaleDateString()}` : ''}
            </div>
          ) : null}
          {item.adminBucket ? (
            <div className="ash-corrected">
              corrected by {item.adminBucketBy || 'admin'}
              {item.adminNote ? <div className="ash-note">{item.adminNote}</div> : null}
            </div>
          ) : null}
          {/* More than one correction means admins have disagreed with each
              other, or with themselves over time — the most interesting signal
              on the card, so it is spelled out rather than collapsed. */}
          {item.corrections?.length > 1 ? (
            <div className="ash-history">
              {item.corrections.slice().reverse().map((c, i) => (
                <div key={i} className="ash-history-row">
                  {TITLE[c.from] || c.from} to {TITLE[c.to] || c.to}
                  {c.by ? ` · ${c.by}` : ''}
                  {c.note ? <div className="ash-note">{c.note}</div> : null}
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
      <div className="ash-moves">
        {ORDER.filter((o) => o !== bucket).map((o) => (
          <button
            key={o}
            className="ash-mv"
            data-to={o}
            disabled={busy || item.manualHidden}
            title={item.manualHidden ? 'Hidden from User Management — manage it there' : undefined}
            onClick={(e) => { e.stopPropagation(); onMove(item, bucket, o); }}
          >
            {LABEL[o]}
          </button>
        ))}
      </div>
    </div>
  );
};

const AiShortlist = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // { item, from, to } while the comment box is open for a move.
  const [pendingMove, setPendingMove] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  // Progressive rendering: the full population is fetched once, but each column
  // paints PAGE_SIZE cards and grows via its own sentinel.
  const [visibleCount, setVisibleCount] = useState({ approved: PAGE_SIZE, maybe: PAGE_SIZE, rejected: PAGE_SIZE });
  const loadMore = useCallback((b) => {
    setVisibleCount((prev) => ({ ...prev, [b]: prev[b] + 100 }));
  }, []);
  // Filters — refetching resets each column back to its first page.
  const [location, setLocation] = useState('all');
  const [joined, setJoined] = useState('all');
  const [from, setFrom] = useState(TODAY);
  const [to, setTo] = useState(TODAY);
  // Search is debounced. It now costs a request rather than a re-filter -- the
  // server searches the whole judged population, not just the rows this board
  // happens to hold -- so the pause is a little longer than it used to be.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    // Normalised exactly the way the server normalises it and no further -- the
    // check below compares this against the echo to know whether the payload on
    // screen answers what is in the box, and any extra step here (a length cap,
    // say) would make the two disagree forever.
    const t = setTimeout(() => setQuery(search.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [search]);
  // Space-separated terms are ANDed, so "gurugram bmw" narrows on both.
  const terms = useMemo(() => query.split(/\s+/).filter(Boolean), [query]);
  const [sort, setSort] = useState(DEFAULT_SORT);
  // Re-ordering or narrowing the board starts each column at page one again --
  // otherwise a column stays scrolled deep into an order that no longer exists.
  useEffect(() => {
    setVisibleCount({ approved: PAGE_SIZE, maybe: PAGE_SIZE, rejected: PAGE_SIZE });
  }, [query, sort]);
  const [openMenu, setOpenMenu] = useState(null);
  const toggleMenu = (m) => (e) => { e.stopPropagation(); setOpenMenu((v) => (v === m ? null : m)); };
  useEffect(() => {
    const close = () => setOpenMenu(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);
  // Card click opens the shared admin profile popup, keyed on the member id.
  const [profileUserId, setProfileUserId] = useState(null);
  const openProfile = useCallback((id) => { if (id) setProfileUserId(id); }, []);
  const closeProfile = useCallback(() => setProfileUserId(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Search and sort are server params now, not browser work. The board used
      // to filter whatever the per-bucket cap had sent it, which stopped being
      // the whole story the moment Rejected outgrew 2,000 -- a search then
      // silently missed everyone past the cap. Sending the terms means they are
      // matched against every judged profile and the cap keeps the right window.
      const d = await getShortlist(2000, { location, joined, from, to, q: query, sort });
      setData(d);
      setVisibleCount({ approved: PAGE_SIZE, maybe: PAGE_SIZE, rejected: PAGE_SIZE });
    } catch (e) {
      setError(e?.message || 'Failed to load shortlist');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, joined, from, to, query, sort]);

  useEffect(() => { load(); }, [load]);

  // While anything is pending, poll gently so judged profiles slide from the
  // pending strip into their bucket without a manual refresh.
  useEffect(() => {
    if (!data?.pending?.count) return undefined;
    const t = setInterval(() => { load(); }, 60000);
    return () => clearInterval(t);
  }, [data?.pending?.count, load]);

  // A move button no longer moves the card on its own — it opens the comment
  // box first. Overruling the AI is the one moment an admin knows something the
  // rubric does not, so it is the only cheap chance to capture the reason; a
  // bucket recorded without one tells a later reader that the AI was wrong but
  // never why. The comment stays optional: "Move without a comment" is one
  // click, so nobody is blocked mid-review.
  const requestMove = useCallback((item, from, to) => {
    setPendingMove({ item, from, to });
    setNoteDraft('');
  }, []);

  const commitMove = useCallback(async (note) => {
    const move = pendingMove;
    if (!move) return;
    const { item, from, to } = move;
    setPendingMove(null);
    setBusyId(item._id);
    // Optimistic move; reload from the server on failure so state never lies.
    setData((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        buckets: { ...prev.buckets },
        counts: { ...prev.counts },
      };
      next.buckets[from] = prev.buckets[from].filter((x) => x._id !== item._id);
      next.buckets[to] = [{ ...item, adminBucket: to, adminNote: note }, ...prev.buckets[to]];
      next.counts[from] -= 1;
      next.counts[to] += 1;
      // `counts` are the matches; `totals` the population behind them. Under a
      // search the card stays a match after the move -- the bucket is not part
      // of what was searched -- so both sides move together or the tiles start
      // disagreeing with each other.
      if (prev.totals) {
        next.totals = { ...prev.totals };
        next.totals[from] -= 1;
        next.totals[to] += 1;
      }
      return next;
    });
    try {
      await setBucket(item._id, to, note);
    } catch (e) {
      await load();
    } finally {
      setBusyId(null);
    }
  }, [load, pendingMove]);

  // Haystacks are built once per payload, not once per keystroke.
  const index = useMemo(() => {
    const m = new Map();
    ORDER.forEach((b) => (data?.buckets?.[b] || []).forEach((it) => m.set(it._id, haystackOf(it))));
    return m;
  }, [data]);

  // Which sorts this payload can actually honour. A sort whose field is absent
  // is never offered, so the control can only ever show orders that work.
  const sortOptions = useMemo(() => {
    // Both writers store `u.createdAt || null`, so the field can be present but
    // null -- which makes a single sample row worthless as evidence: one member
    // with no signup date would hide a sort the server fully supports. Ask
    // whether ANY loaded row carries a value instead. If none does, the sort has
    // nothing to order by anyway, so hiding it is right either way.
    const anyRowHas = (field) => ORDER.some((b) => (data?.buckets?.[b] || []).some((it) => it[field] != null));
    return SORTS.filter(([k]) => !SORT_NEEDS[k] || anyRowHas(SORT_NEEDS[k]));
  }, [data]);
  // If the active sort is not on offer (older server, or the field vanished),
  // fall back rather than render an order the data cannot support.
  const activeSort = sortOptions.some(([k]) => k === sort) ? sort : DEFAULT_SORT;

  // Does this server honour `q`/`sort` at all? Only the wording of the capped-
  // column line turns on this; an older one ignores both params and sends
  // everything, which is a genuinely narrower answer and has to say so.
  const serverSearch = !!data?.search;
  // Does the payload on screen answer the query in the box RIGHT NOW? Between a
  // keystroke settling and its response landing it does not, and trusting its
  // counts for that beat would flash "908 of 3,675 match" over an unsearched
  // board. While they disagree the browser narrows what it already holds --
  // the old behaviour, correct as far as the loaded rows go — and the response
  // replaces it a moment later with the answer over the whole population.
  const serverFiltered = data?.search?.q === query;

  // The single filtered view EVERY consumer below renders from -- cards, column
  // pills and the stat tiles all read this one object, so a searched board can
  // never show a tile count that disagrees with the cards underneath it.
  const view = useMemo(() => {
    const blank = { approved: [], maybe: [], rejected: [] };
    const zero = { approved: 0, maybe: 0, rejected: 0 };
    if (!data) {
      return { buckets: blank, counts: zero, totals: zero, scanned: 0, scannedTotal: 0, avgApprovedScore: null, pending: null, capped: [] };
    }
    // Only a server that searched for us sends `totals`; against an older one
    // the counts ARE the whole population, because nothing was narrowed away.
    const totals = data.totals || data.counts;
    const scannedTotal = data.scannedTotal ?? data.scanned;
    // A column is capped when the server matched more rows than it sent. That
    // is now a paging limit rather than a blind spot -- the search and the sort
    // both ran over everything -- but the board still has to say which columns
    // are showing a window instead of the whole set.
    const capped = ORDER.filter((b) => (data.buckets[b]?.length || 0) < (data.counts[b] || 0))
      .map((b) => ({ bucket: b, shown: data.buckets[b]?.length || 0, of: data.counts[b] || 0 }));
    const cmp = COMPARE[activeSort] || COMPARE[DEFAULT_SORT];
    const hit = (hay) => terms.every((t) => hay.includes(t));
    const narrow = terms.length > 0 && !serverFiltered;
    const buckets = {};
    const counts = {};
    let scanned = 0;
    ORDER.forEach((b) => {
      const src = data.buckets[b] || [];
      // Copy before sorting: `data` is the fetched payload and the optimistic
      // move reads from it, so re-ordering it in place would corrupt state.
      const rows = narrow
        ? src.filter((it) => hit(index.get(it._id) ?? haystackOf(it)))
        : src.slice();
      rows.sort(cmp);
      buckets[b] = rows;
      counts[b] = narrow ? rows.length : (data.counts[b] ?? rows.length);
      scanned += counts[b];
    });
    const appr = buckets.approved;
    return {
      buckets,
      counts,
      scanned,
      totals,
      scannedTotal,
      avgApprovedScore: narrow
        ? (appr.length ? Math.round(appr.reduce((sum, x) => sum + (x.score || 0), 0) / appr.length) : null)
        : data.avgApprovedScore,
      // Server-side, the pending strip is searched too, so its count is a real
      // count of matches. Filtering it here can only ever narrow the 30-row
      // SAMPLE the server sent -- hence `sampled`, which makes the strip say so.
      pending: narrow && data.pending ? {
        ...data.pending,
        items: data.pending.items.filter((pd) => hit(`${pd.name || ''} ${pd.city || ''}`.toLowerCase())),
        sampled: true,
      } : data.pending,
      capped,
    };
  }, [data, index, terms, activeSort, serverFiltered]);

  const counts = view.counts;
  const searching = terms.length > 0;
  // Judging has no location scope of its own (Delhi-NCR → India → every
  // country, 2026-08-17), so the count under Profiles Judged is whatever the
  // Location filter says — name that filter rather than a fixed region.
  const scopeLabel = location === 'all'
    ? 'every location'
    : (LOCATIONS.find(([k]) => k === location) || LOCATIONS[0])[1];

  return (
    <>
      <style>{styles}</style>
      <div className="favor-ash">
        <div className="ash-head">
          <div>
            <div className="ash-title">AI Shortlist Review</div>
            <div className="ash-sub">
              Male profiles pre-sorted by the photo judge (Fable&nbsp;5), calibrated on this
              team&apos;s own hide/keep decisions. Moving a card corrects the AI and applies
              search visibility immediately; new and photo-changed profiles are judged
              automatically. Only Approved members appear in search.
            </div>
          </div>
        </div>

        <div className="ash-filters">
          <div className="ash-search">
            <span className="ash-search-ico" aria-hidden="true">&#9906;</span>
            <input
              type="text"
              value={search}
              placeholder="Search name, email, city, or the AI's reasoning..."
              aria-label="Search judged profiles"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setSearch(''); }}
            />
            {search ? (
              <button className="ash-search-x" title="Clear search" onClick={() => setSearch('')}>&times;</button>
            ) : null}
          </div>
          <div className="ash-dd">
            <div className={`ash-pillfilter ${location !== 'all' ? 'on' : ''}`} onClick={toggleMenu('location')}>
              Location: <b>{(LOCATIONS.find(([k]) => k === location) || LOCATIONS[0])[1]}</b><span className="ash-car" />
            </div>
            <div className={`ash-ddmenu ${openMenu === 'location' ? 'open' : ''}`}>
              {LOCATIONS.map(([k, l]) => (
                <div key={k} className={`ash-dditem ${location === k ? 'sel' : ''}`} onClick={() => { setLocation(k); setOpenMenu(null); }}>{l}</div>
              ))}
            </div>
          </div>
          <div className="ash-dd">
            <div className={`ash-pillfilter ${joined !== 'all' ? 'on' : ''}`} onClick={toggleMenu('joined')}>
              Joined: <b>{(JOINED.find(([k]) => k === joined) || JOINED[3])[1]}</b><span className="ash-car" />
            </div>
            <div className={`ash-ddmenu ${openMenu === 'joined' ? 'open' : ''}`}>
              {JOINED.map(([k, l]) => (
                <div key={k} className={`ash-dditem ${joined === k ? 'sel' : ''}`} onClick={() => { setJoined(k); setOpenMenu(null); }}>{l}</div>
              ))}
            </div>
          </div>
          {joined === 'custom' && (
            <span className="ash-daterange" onClick={(e) => e.stopPropagation()}>
              <input type="date" max={TODAY} value={from} onChange={(e) => { const v = e.target.value; setFrom(v); if (v > to) setTo(v); }} />
              <span className="ash-dsep">to</span>
              <input type="date" max={TODAY} value={to} onChange={(e) => { const v = e.target.value; setTo(v); if (v < from) setFrom(v); }} />
            </span>
          )}
          <div className="ash-dd">
            <div className={`ash-pillfilter ${activeSort !== DEFAULT_SORT ? 'on' : ''}`} onClick={toggleMenu('sort')}>
              Sort: <b>{(sortOptions.find(([k]) => k === activeSort) || SORTS[0])[1]}</b><span className="ash-car" />
            </div>
            <div className={`ash-ddmenu ${openMenu === 'sort' ? 'open' : ''}`}>
              {sortOptions.map(([k, l]) => (
                <div key={k} className={`ash-dditem ${activeSort === k ? 'sel' : ''}`} onClick={() => { setSort(k); setOpenMenu(null); }}>{l}</div>
              ))}
            </div>
          </div>
        </div>

        {(searching || view.capped.length > 0) && data ? (
          <div className="ash-searchsum">
            {searching ? (
              <span>
                <b>{view.scanned}</b> of {view.scannedTotal} judged profiles match <span className="ash-q">{query}</span>
              </span>
            ) : null}
            {/* Terms are ANDed, so a dropped one makes this list WIDER than what
                was typed -- the one kind of "too many" that cannot be left to
                speak for itself, because it looks like a normal result. */}
            {serverFiltered && data.search?.termsDropped > 0 ? (
              <span className="ash-warn">
                only the first {data.search.terms.length} words were used &mdash; the rest widen this list rather than narrow it
              </span>
            ) : null}
            {/* A capped column means two different things depending on who did
                the searching, so it cannot be one sentence. Server-side, the
                search and the sort covered everything and the column is simply
                showing its first page. Against an older server they only ever
                saw the rows that arrived, which is a real blind spot. */}
            {view.capped.length ? (
              <span className={serverSearch ? undefined : 'ash-warn'}>
                {serverSearch
                  ? `showing ${view.capped.map((c) => `${TITLE[c.bucket]} ${c.shown} of ${c.of}`).join(' · ')} — search and sort ran over every judged profile, so narrowing reaches the rest`
                  : 'showing the loaded slice only — a bucket is capped by the server, so search and sort do not cover the remainder'}
              </span>
            ) : null}
            {searching ? <button className="ash-clear" onClick={() => setSearch('')}>Clear</button> : null}
          </div>
        ) : null}

        {error ? <div className="ash-error">{error}</div> : null}
        {!loading && !error && location === 'us' && data && view.scannedTotal === 0 && !data.pending?.count ? (
          <div className="ash-applyresult">
            No United States profiles have been judged yet. Curation now covers every
            country, and profiles are judged automatically once they complete a photo —
            so this fills in as they arrive. Nothing is missing or broken.
          </div>
        ) : null}
        {loading ? <Loader /> : null}

        {data ? (
          <>
            <div className="ash-stats">
              <div className="ash-stat" style={{ '--bar': '#2563eb' }}>
                <div className="lab">Profiles Judged</div>
                <div className="val">{view.scanned}</div>
                <div className="hint">{searching ? `of ${view.scannedTotal} judged` : `${scopeLabel} · male`}</div>
              </div>
              <div className="ash-stat" style={{ '--bar': '#1e7f4f' }}>
                <div className="lab">Approved (Elite)</div>
                <div className="val">{counts.approved}</div>
                <div className="hint">{searching ? `of ${view.totals.approved}` : 'ready to surface'}</div>
              </div>
              <div className="ash-stat" style={{ '--bar': '#b7791f' }}>
                <div className="lab">Maybe</div>
                <div className="val">{counts.maybe}</div>
                <div className="hint">{searching ? `of ${view.totals.maybe}` : 'clean, not elite'}</div>
              </div>
              <div className="ash-stat" style={{ '--bar': '#c0392b' }}>
                <div className="lab">Rejected</div>
                <div className="val">{counts.rejected}</div>
                <div className="hint">{searching ? `of ${view.totals.rejected}` : 'photo fails the bar'}</div>
              </div>
              <div className="ash-stat" style={{ '--bar': '#c9a35e' }}>
                <div className="lab">Avg Score</div>
                <div className="val">{view.avgApprovedScore ?? '–'}</div>
                <div className="hint">{searching ? 'approved matches' : 'approved bucket'}</div>
              </div>
            </div>

{view.pending?.count ? (
              <div className="ash-pending">
                <div className="ash-pending-head">
                  <span className="ash-pending-title">⏳ Pending AI review</span>
                  <span className="ash-pill">{view.pending.count}</span>
                  <span className="ash-pending-hint">
                    new or photo-changed profiles awaiting judgment (matches the filters
                    {searching && !view.pending.sampled ? ' and search' : ''} above) — judged within
                    seconds of the change, 5-min sweep as backstop; they land in their bucket automatically.
                    {view.pending.sampled
                      ? ' Search only narrows the sample shown here, not the full pending count.'
                      : ''}
                  </span>
                </div>
                <div className="ash-pending-row">
                  {view.pending.sampled && !view.pending.items.length ? (
                    <div className="ash-pmore">no pending profile in the loaded sample matches this search</div>
                  ) : null}
                  {view.pending.items.map((p) => (
                    <div className="ash-pcard" key={p.userId} onClick={() => openProfile(p.userId)}>
                      <div className="ash-avatar">
                        {p.profilePicture
                          ? <img src={p.profilePicture} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          : initials(p.name)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="ash-pname">{p.name || '—'}{p.age ? `, ${p.age}` : ''}</div>
                        <div className="ash-meta">{p.city || ''}{p.reEval ? ' · photo changed' : ''}</div>
                      </div>
                    </div>
                  ))}
                  {!view.pending.sampled && view.pending.count > view.pending.items.length ? (
                    <div className="ash-pmore">+{view.pending.count - view.pending.items.length} more</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="ash-buckets-scroll">
              <div className="ash-buckets">
                {ORDER.map((b) => (
                  <div className="ash-bucket" data-bucket={b} key={b}>
                    <div className="ash-bucket-head">
                      <div className="ash-bucket-title"><span className="ash-dot" />{TITLE[b]}</div>
                      <div className="ash-pill">{counts[b]}</div>
                    </div>
                    <div className="ash-bucket-note">{NOTE[b]}</div>
                    {view.buckets[b]?.length ? (
                      view.buckets[b].slice(0, visibleCount[b]).map((item) => (
                        <Card
                          key={item._id}
                          item={item}
                          bucket={b}
                          onMove={requestMove}
                          onOpen={openProfile}
                          busy={busyId === item._id}
                        />
                      ))
                    ) : (
                      <div className="ash-empty">
                        {searching ? 'No matches in this bucket.' : 'No profiles in this bucket.'}
                      </div>
                    )}
                    {(view.buckets[b]?.length || 0) > visibleCount[b] ? (
                      <Sentinel onVisible={() => loadMore(b)} />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : null}

        {pendingMove ? (
          <div className="ash-modal" role="dialog" aria-modal="true" aria-label="Comment on this move">
            <div className="ash-modal-card">
              <div className="ash-modal-title">
                Move to <span className={`ash-dest ${pendingMove.to}`}>{TITLE[pendingMove.to]}</span>
              </div>
              <div className="ash-modal-who">
                {pendingMove.item.name || '—'}{pendingMove.item.age ? `, ${pendingMove.item.age}` : ''}
                {pendingMove.item.city ? ` · ${pendingMove.item.city}` : ''}
                {' · AI said '}
                {pendingMove.item.tier}{pendingMove.item.elite ? '/elite' : ''}
              </div>
              <label className="ash-modal-label" htmlFor="ash-note">
                Why is {TITLE[pendingMove.from]} wrong for this profile? (optional)
              </label>
              <textarea
                id="ash-note"
                className="ash-modal-input"
                autoFocus
                rows={3}
                maxLength={500}
                placeholder="e.g. gym mirror selfie, not a composed shot"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setPendingMove(null);
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commitMove(noteDraft.trim());
                }}
              />
              <div className="ash-modal-hint">
                {noteDraft.length}/500 · Comments are collected to sharpen the AI&apos;s rubric later.
                They do not change how it judges today.
              </div>
              <div className="ash-modal-actions">
                <button type="button" className="ash-modal-btn ghost" onClick={() => setPendingMove(null)}>
                  Cancel
                </button>
                <button type="button" className="ash-modal-btn ghost" onClick={() => commitMove('')}>
                  Move without a comment
                </button>
                <button
                  type="button"
                  className="ash-modal-btn primary"
                  disabled={!noteDraft.trim()}
                  onClick={() => commitMove(noteDraft.trim())}
                >
                  Save comment &amp; move
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* UserProfileMainPage fetches once on mount, so it is KEYED on the id —
          reopening with another member remounts it rather than showing stale data. */}
      <Modal open={!!profileUserId} onClose={closeProfile} aria-labelledby="ai-shortlist-profile-modal" sx={{ zIndex: 1500 }}>
        <Box sx={PROFILE_MODAL_SX}>
          <Box sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10, padding: '10px' }}>
            <IconButton sx={{ position: 'absolute', top: 10, right: 0 }} onClick={closeProfile}>
              <CloseIcon />
            </IconButton>
          </Box>
          {profileUserId && <UserProfileMainPage key={profileUserId} userId={profileUserId} />}
        </Box>
      </Modal>
    </>
  );
};

export default AiShortlist;
